import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaEye, FaPaperPlane, FaTrash, FaPrint, FaFileExcel, FaEdit, FaPaperclip, FaFileAlt, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { toast } from 'react-toastify';
import {
  getReceiptVouchers,
  getReceiptVoucherDetails,
  postReceiptVoucher,
  deleteReceiptVoucher,
  getPostingAccounts,
  getBanksAndSafes,
  getProjects,
  clearError,
  clearSuccess,
} from '../../../../redux/Slices/authSlice';
import { useTitle } from '../../../../shared/hooks/TitleContext';
import {
  generatePdfFromElementIds,
  generateSingleElementPdf,
  waitForRender,
  datedFileName,
} from '../../../../shared/pdf/pdfEngine';
import * as XLSX from 'xlsx';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n/i18n';
import PdfPreviewModal from '../Reports/components/PdfPreviewModal';
import { calculatePdfPagination, convertArabicNumbersToEnglish, BASE_URL } from '../../../../shared/utils.js/utils';

const ReceiptVouchers = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setTitle } = useTitle();
  const {
    receiptVouchers,
    receiptVouchersPagination,
    receiptVoucherDetails,
    postingAccounts,
    banksAndSafes,
    projects,
    projectsPagination,
    isLoading,
    error,
    success,
  } = useSelector((state) => state.auth);

  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortField, setSortField] = useState('id');
  const [sortOrder, setSortOrder] = useState('desc');
  const [projectId, setProjectId] = useState('');
  const [accountId, setAccountId] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  const [projectCurrentPage, setProjectCurrentPage] = useState(1);
  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false);
  const [accountSearchTerm, setAccountSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [voucherToDelete, setVoucherToDelete] = useState(null);
  const [voucherForPrint, setVoucherForPrint] = useState(null);
  const [voucherForPreview, setVoucherForPreview] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfType, setPdfType] = useState(null); // 'single' or 'all'
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [allReceiptVouchers, setAllReceiptVouchers] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [pdfChunks, setPdfChunks] = useState([]);
  const [allVouchersForPDF, setAllVouchersForPDF] = useState([]);
  const [perPage, setPerPage] = useState(10);
  const [perPageInput, setPerPageInput] = useState(10);
  const lastErrorRef = useRef({ message: null, time: 0 });
  const searchDebounceTimeoutRef = useRef(null);
  const vouchersThrottleTimeoutRef = useRef(null);
  const vouchersLastDispatchRef = useRef(0);
  const projectSearchTimeoutRef = useRef(null);
  const projectSearchInputRef = useRef(null);
  const VOUCHERS_THROTTLE_MS = 500;
  const [attachmentsModalOpen, setAttachmentsModalOpen] = useState(false);
  const [selectedAttachments, setSelectedAttachments] = useState([]);
  const { t } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';
  const publicUrl = process.env.PUBLIC_URL || '';
  const headerLogo = `${publicUrl}/ACE.png`;

  const STATUS_FILTER_ENUM = Object.freeze({
    DRAFT: 'draft',
    POSTED: 'posted',
  });

  const normalizeDateForApi = (value) => {
    if (!value) return undefined;
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
    const match = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!match) return undefined;
    const day = match[1].padStart(2, '0');
    const month = match[2].padStart(2, '0');
    const year = match[3];
    return `${year}-${month}-${day}`;
  };

  const accountsOptions = useMemo(() => {
    const posting = Array.isArray(postingAccounts) ? postingAccounts : [];
    const banks = Array.isArray(banksAndSafes) ? banksAndSafes : [];
    const merged = [...banks, ...posting];
    const uniqueById = new Map();
    merged.forEach((acc) => {
      const id = String(acc?.id ?? acc?.account_id ?? '');
      if (id && !uniqueById.has(id)) uniqueById.set(id, acc);
    });
    return Array.from(uniqueById.values());
  }, [postingAccounts, banksAndSafes]);

  const filteredAccounts = useMemo(() => {
    const term = accountSearchTerm.trim().replace(/\s+/g, ' ').toLowerCase();
    if (!term) return accountsOptions;
    return accountsOptions.filter((acc) => {
      const nameAr = String(acc?.name_ar || '').toLowerCase();
      const nameEn = String(acc?.name_en || '').toLowerCase();
      const name = String(acc?.name || '').toLowerCase();
      const code = String(acc?.code || '').toLowerCase();
      return nameAr.includes(term) || nameEn.includes(term) || name.includes(term) || code.includes(term);
    });
  }, [accountsOptions, accountSearchTerm]);

  useEffect(() => {
    setTitle('sidenav.receiptVouchers');
  }, [setTitle]);

  useEffect(() => {
    dispatch(getPostingAccounts());
    dispatch(getBanksAndSafes());
  }, [dispatch]);

  useEffect(() => {
    if (!projectDropdownOpen) return;
    dispatch(getProjects({ page: projectCurrentPage, per_page: 10, search: projectSearchTerm || undefined }));
  }, [dispatch, projectDropdownOpen, projectCurrentPage, projectSearchTerm]);

  useEffect(() => {
    if (searchDebounceTimeoutRef.current) clearTimeout(searchDebounceTimeoutRef.current);
    searchDebounceTimeoutRef.current = setTimeout(() => {
      setCurrentPage(1);
      setDebouncedSearch(search);
    }, 400);
    return () => {
      if (searchDebounceTimeoutRef.current) clearTimeout(searchDebounceTimeoutRef.current);
    };
  }, [search]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('[data-dropdown-container]')) {
        setProjectDropdownOpen(false);
        setAccountDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const buildVoucherFilters = (searchValue = debouncedSearch) => {
    const normalisedSearch = convertArabicNumbersToEnglish(searchValue).replace(/\s+/g, ' ').trim();
    return {
      page: currentPage,
      per_page: perPage,
      search: normalisedSearch,
      project_id: projectId.trim(),
      account_id: accountId.trim(),
      status: Object.values(STATUS_FILTER_ENUM).includes(statusFilter) ? statusFilter : '',
      date_from: normalizeDateForApi(dateFrom) ?? '',
      date_to: normalizeDateForApi(dateTo) ?? '',
      sort_by: sortField,
      sort_order: sortOrder,
    };
  };

  // Fetch receipt vouchers for table (per_page = perPage state)
  const fetchReceiptVouchers = async () => {
    try {
      const params = buildVoucherFilters();
      const result = await dispatch(getReceiptVouchers(params));
      if (getReceiptVouchers.fulfilled && result.type === getReceiptVouchers.fulfilled.type) {
        const response = result.payload;
        const data = response?.data || response;
        const paginationData = data?.meta || data?.pagination || receiptVouchersPagination || null;
        let vouchersList = [];
        if (Array.isArray(data?.data)) {
          vouchersList = data.data;
        } else if (Array.isArray(data)) {
          vouchersList = data;
        }
        const total = paginationData?.total || vouchersList.length;
        const currentPageSafe = Math.max(1, currentPage);
        const startIndex = (currentPageSafe - 1) * perPage;
        const listWithSerials = vouchersList.map((voucher, idx) => ({
          ...voucher,
          _serialNumber: sortOrder === 'desc'
            ? total - (startIndex + idx)
            : startIndex + idx + 1
        }));
        setAllReceiptVouchers(listWithSerials);
        setPagination(paginationData);
      }
    } catch (err) {
    }
  };

  // Fetch all receipt vouchers for PDF templates (per_page = total)
  const fetchAllReceiptVouchersForPDF = async () => {
    try {
      // First fetch to get total count
      const params = {
        ...buildVoucherFilters(),
        page: 1,
        per_page: 10
      };

      const result = await dispatch(getReceiptVouchers(params));
      if (getReceiptVouchers.fulfilled && result.type === getReceiptVouchers.fulfilled.type) {
        const response = result.payload;
        const data = response?.data || response;
        const paginationData = data?.meta || data?.pagination || receiptVouchersPagination || null;
        const total = paginationData?.total || 0;

        if (total > 0) {
          // Fetch all vouchers for PDF
          const allParams = {
            ...buildVoucherFilters(),
            page: 1,
            per_page: total
          };

          const allResult = await dispatch(getReceiptVouchers(allParams));
          if (getReceiptVouchers.fulfilled && allResult.type === getReceiptVouchers.fulfilled.type) {
            const allResponse = allResult.payload;
            const allData = allResponse?.data || allResponse;
            let vouchersList = [];
            if (Array.isArray(allData?.data)) {
              vouchersList = allData.data;
            } else if (Array.isArray(allData)) {
              vouchersList = allData;
            }
            const totalVal = vouchersList.length;
            const listWithSerials = vouchersList.map((voucher, idx) => ({
              ...voucher,
              _serialNumber: sortOrder === 'desc'
                ? totalVal - idx
                : idx + 1
            }));
            setAllVouchersForPDF(listWithSerials);
            return listWithSerials;
          }
        }
      }
      return [];
    } catch (err) {
      return [];
    }
  };

  useEffect(() => {
    const throttledFetch = () => {
      const now = Date.now();
      const elapsed = now - vouchersLastDispatchRef.current;
      const remaining = Math.max(VOUCHERS_THROTTLE_MS - elapsed, 0);

      if (vouchersThrottleTimeoutRef.current) clearTimeout(vouchersThrottleTimeoutRef.current);

      if (remaining === 0) {
        vouchersLastDispatchRef.current = Date.now();
        fetchReceiptVouchers();
        return;
      }

      vouchersThrottleTimeoutRef.current = setTimeout(() => {
        vouchersLastDispatchRef.current = Date.now();
        fetchReceiptVouchers();
      }, remaining);
    };

    throttledFetch();
    return () => {
      if (vouchersThrottleTimeoutRef.current) clearTimeout(vouchersThrottleTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, currentPage, perPage, debouncedSearch, projectId, accountId, statusFilter, dateFrom, dateTo, sortField, sortOrder, i18n.language]);

  useEffect(() => {
    if (success) {
      toast.success(success, {
        rtl: isRTL,
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
      // Only refresh when adding new voucher
      if (success.includes('إضافة') || success.includes('تم إضافة') || success.includes('Add') || success.includes('added')) {
        const params = buildVoucherFilters(search);
        dispatch(getReceiptVouchers(params));
      }
      // Don't refresh on delete - it updates optimistically
      dispatch(clearSuccess());
    }
  }, [success, dispatch, isRTL]);

  useEffect(() => {
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, {
          rtl: isRTL,
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [error, dispatch, isRTL]);

  const openDeleteModal = (voucher) => {
    setVoucherToDelete(voucher);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setVoucherToDelete(null);
    setShowDeleteModal(false);
  };

  const handleApplyPerPage = () => {
    const validValue = Math.max(1, Math.min(perPageInput, 1000));
    setPerPage(validValue);
    setCurrentPage(1);
  };

  const applyFilters = () => {
    setCurrentPage(1);
    setDebouncedSearch(search);
    dispatch(getReceiptVouchers({
      ...buildVoucherFilters(search),
      page: 1,
    }));
  };

  const resetFilters = () => {
    setSearch('');
    setDebouncedSearch('');
    setProjectId('');
    setAccountId('');
    setProjectSearchTerm('');
    setAccountSearchTerm('');
    setProjectCurrentPage(1);
    setStatusFilter('');
    setDateFrom('');
    setDateTo('');
    setCurrentPage(1);
    setSortField('id');
    setSortOrder('desc');
  };

  const renderAccountDisplay = (id) => {
    if (!id) return t('receiptVouchers.form.selectAccount', { defaultValue: 'Select Account' });
    const selected = accountsOptions.find((acc) => String(acc?.id ?? acc?.account_id) === String(id));
    if (!selected) return t('receiptVouchers.form.selectAccount', { defaultValue: 'Select Account' });
    const name = isRTL
      ? (selected.name_ar || selected.name || selected.name_en || '')
      : (selected.name_en || selected.name || selected.name_ar || '');
    return `${selected.code ? `${selected.code} - ` : ''}${name}`;
  };

  const renderProjectDisplay = (id) => {
    if (!id) return t('receiptVouchers.form.selectProject', { defaultValue: 'Select Project' });
    const selected = (projects || []).find((p) => String(p.id) === String(id));
    if (!selected) return t('receiptVouchers.form.selectProject', { defaultValue: 'Select Project' });
    const name = isRTL ? (selected.name_ar || selected.name_en || '') : (selected.name_en || selected.name_ar || '');
    return `${selected.code ? `${selected.code} - ` : ''}${name}`;
  };

  const handleProjectSearchChange = (value) => {
    setProjectSearchTerm(value);
    setProjectCurrentPage(1);
    if (projectSearchTimeoutRef.current) clearTimeout(projectSearchTimeoutRef.current);
    projectSearchTimeoutRef.current = setTimeout(() => {
      dispatch(getProjects({ page: 1, per_page: 10, search: value || undefined }));
    }, 300);
  };

  const sortVouchersList = (list) => {
    return list;
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  // Use receipt vouchers from API (already paginated with per_page)
  const sortedReceiptVouchers = allReceiptVouchers;
  const filteredReceiptVouchers = sortedReceiptVouchers || [];
  const currentPageSafe = Math.max(1, currentPage);
  const totalPages = pagination?.last_page || Math.ceil((pagination?.total || 0) / 10) || 1;
  const paginatedReceiptVouchers = filteredReceiptVouchers; // Already paginated from API
  const startIndex = (currentPageSafe - 1) * perPage;

  const activeFiltersForPdf = useMemo(() => {
    const parts = [];
    if (debouncedSearch) parts.push(`${t('common.search', { defaultValue: 'بحث' })}: ${debouncedSearch}`);
    if (projectId) {
      const proj = (projects || []).find(p => String(p.id) === String(projectId));
      const projName = proj ? (isRTL ? (proj.name_ar || proj.name_en) : (proj.name_en || proj.name_ar)) : projectId;
      parts.push(`${t('receiptVouchers.form.project', { defaultValue: 'المشروع' })}: ${projName}`);
    }
    if (accountId) {
      const acc = accountsOptions.find(a => String(a?.id ?? a?.account_id) === String(accountId));
      const accName = acc ? `${acc.code ? acc.code + ' - ' : ''}${isRTL ? (acc.name_ar || acc.name) : (acc.name_en || acc.name)}` : accountId;
      parts.push(`${t('receiptVouchers.tableHeaders.account', { defaultValue: 'Account' })}: ${accName}`);
    }
    if (statusFilter) parts.push(`${t('receiptVouchers.tableHeaders.status', { defaultValue: 'Status' })}: ${t(`receiptVouchers.status.${statusFilter}`, { defaultValue: statusFilter })}`);
    if (dateFrom) parts.push(`${t('common.from', { defaultValue: 'من' })}: ${dateFrom}`);
    if (dateTo) parts.push(`${t('common.to', { defaultValue: 'إلى' })}: ${dateTo}`);
    return parts.join(' | ');
  }, [debouncedSearch, projectId, accountId, statusFilter, dateFrom, dateTo, projects, accountsOptions, isRTL, t]);

  const confirmDelete = async () => {
    if (!voucherToDelete) return;
    const idToDelete = voucherToDelete.id;

    // Don't do optimistic delete - we can't know if voucher is linked to another system
    // Only remove from local state after successful deletion
    closeDeleteModal();
    setVoucherToDelete(null);

    try {
      const result = await dispatch(deleteReceiptVoucher(idToDelete));
      // Check if the action was rejected
      if (deleteReceiptVoucher.rejected.match(result)) {
        throw new Error(result.payload || 'فشل حذف سند القبض');
      }
      // Remove from local state after successful deletion
      setAllReceiptVouchers(prev => prev.filter(voucher => voucher.id !== idToDelete));
      // Error will be shown by useEffect if needed
    } catch (error) {
      // Don't show error here - useEffect will handle it
      // Voucher remains in the list on error
    }
  };

  const handlePost = (id) => {
    dispatch(postReceiptVoucher(id));
  };

  const openAttachmentsModal = (attachments = []) => {
    if (!attachments || attachments.length === 0) return;
    setSelectedAttachments(attachments);
    setAttachmentsModalOpen(true);
  };

  const closeAttachmentsModal = () => {
    setSelectedAttachments([]);
    setAttachmentsModalOpen(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      const locale = 'en-US';
      return date.toLocaleDateString(locale, { year: 'numeric', month: '2-digit', day: '2-digit' });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '0.00';
    const locale = 'en-US';
    return parseFloat(amount).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Handle preview single receipt voucher
  const handlePreviewSingle = async (voucher) => {
    try {
      setIsLoadingDetails(true);
      const result = await dispatch(getReceiptVoucherDetails(voucher.id)).unwrap();
      const voucherData = result?.data || result;
      setVoucherForPreview(voucherData);
      setIsPreviewOpen(true);
    } catch (error) {
      toast.error(error || t('receiptVouchers.preview.error', { defaultValue: 'حدث خطأ أثناء تحميل تفاصيل الإيصال' }), { rtl: isRTL });
    } finally {
      setIsLoadingDetails(false);
    }
  };

  // Preload image helper
  const preloadImage = (src) =>
    new Promise((resolve) => {
      if (!src) return resolve();
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve();
      img.onerror = () => resolve();
      img.src = src;
    });

  // Handle print single receipt voucher
  const handlePrintReceiptVoucher = async (voucher) => {
    try {
      // If voucher doesn't have details, fetch them first
      let voucherData = voucher;
      if (!voucher.details || !voucher.account) {
        try {
          const result = await dispatch(getReceiptVoucherDetails(voucher.id)).unwrap();
          voucherData = result?.data || result;
        } catch (error) {
          // Continue with the voucher data we have
        }
      }

      setVoucherForPrint(voucherData);
      setPdfType('single');
      await new Promise((resolve) => requestAnimationFrame(() => resolve()));

      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }

      // Preload images
      await Promise.all([
        preloadImage(`${publicUrl}/HeaderAce.png`),
        preloadImage(`${publicUrl}/FooterAce.png`),
        preloadImage(headerLogo)
      ]);
      await new Promise((resolve) => setTimeout(resolve, 800));

      setIsGeneratingPdf(true);

      // Retry mechanism for element
      let element = null;
      let retries = 0;
      const maxRetries = 15;

      while (!element && retries < maxRetries) {
        await new Promise((resolve) => requestAnimationFrame(resolve));
        element = document.getElementById(`receipt-voucher-pdf-${voucherData.id}`);
        if (!element) {
          await new Promise((resolve) => setTimeout(resolve, 100));
          retries++;
        }
      }

      if (!element) {
        toast.error(t('receiptVouchers.print.error', { defaultValue: 'خطأ في تحميل قالب الطباعة' }), { rtl: isRTL });
        setIsGeneratingPdf(false);
        return;
      }

      // Additional wait
      await waitForRender(200);

      // Shared PDF Engine — capture + assembly
      const identifier = voucherData.reference || voucherData.voucher_number || voucherData.id;
      await generateSingleElementPdf({
        elementId: `receipt-voucher-pdf-${voucherData.id}`,
        fileName: datedFileName(`ACE_ReceiptVoucher_${identifier}`),
        isRTL,
      });
    } catch (err) {
      toast.error(t('receiptVouchers.print.error', { defaultValue: 'حدث خطأ أثناء إنشاء PDF' }), { rtl: isRTL });
    } finally {
      setIsGeneratingPdf(false);
      setVoucherForPrint(null);
      setPdfType(null);
    }
  };

  // Handle preview all receipt vouchers
  const buildDynamicPdfChunks = (vouchers) => {
    if (!Array.isArray(vouchers) || vouchers.length === 0) {
      setPdfChunks([]);
      return [];
    }

    const elements = {
      topHeader: document.getElementById('receipt-vouchers-pdf-measure-top-header'),
      thead: document.getElementById('receipt-vouchers-pdf-measure-thead'),
      footer: document.getElementById('receipt-vouchers-pdf-measure-footer'),
      tbody: document.getElementById('receipt-vouchers-pdf-measure-tbody'),
    };

    // Dynamic fallback: estimate rows per page without magic numbers
    // A4 inner content area = 1033px - 18px top - 10px bottom - ~180px header - ~32px thead - ~120px footer - 15px margin
    // (footer reserve matches defaultFooterHeight/paddingBottom below — keep both in sync)
    const estimatedRowHeight = 30;
    const estimatedAvailable = 1033 - 18 - 10 - 180 - 32 - 120 - 15;
    const dynamicFallbackSize = Math.max(5, Math.floor(estimatedAvailable / estimatedRowHeight));

    const chunks = calculatePdfPagination(vouchers, elements, {
      pageHeight: 1033,   // A4 @ 96dpi inner bordered div
      containerTopPadding: 18,
      containerBottomPadding: 10,
      safeMargin: 15,
      fallbackSize: dynamicFallbackSize,
      defaultHeaderHeight: 180,
      defaultTableHeaderHeight: 32,
      defaultFooterHeight: 120,
      defaultRowHeight: 28,
    });

    setPdfChunks(chunks);
    return chunks;
  };

  const prepareAllVouchersPdfData = async () => {
    const allVouchers = await fetchAllReceiptVouchersForPDF();
    if (!allVouchers || allVouchers.length === 0) {
      toast.error(t('receiptVouchers.print.noData', { defaultValue: 'No data available' }), {
        rtl: isRTL,
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
      return [];
    }

    const sortedAllVouchers = sortVouchersList(allVouchers);
    setAllVouchersForPDF(sortedAllVouchers);

    // Preload image assets to ensure measurements are accurate
    await Promise.all([
      preloadImage(`${publicUrl}/HeaderAce.png`),
      preloadImage(`${publicUrl}/FooterAce.png`),
      preloadImage(headerLogo)
    ]);

    await new Promise((resolve) => setTimeout(resolve, 200));
    await new Promise((resolve) => requestAnimationFrame(resolve));
    await new Promise((resolve) => requestAnimationFrame(resolve));

    return buildDynamicPdfChunks(sortedAllVouchers);
  };

  const handlePreviewAll = async () => {
    try {
      const chunks = await prepareAllVouchersPdfData();
      if (!chunks || chunks.length === 0) return;
      setIsPreviewOpen(true);
    } catch (err) {
      toast.error(t('receiptVouchers.print.error', { defaultValue: 'An error occurred' }), {
        rtl: isRTL,
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    }
  };

  // Handle print all receipt vouchers
  const handlePrintAll = async () => {
    try {
      setIsGeneratingPdf(true);
      // Always re-fetch fresh data — stale pdfChunks may reflect different filters or old preview state
      const chunks = await prepareAllVouchersPdfData();
      if (!chunks || chunks.length === 0) {
        setIsGeneratingPdf(false);
        return;
      }

      // Wait for React to render the hidden print templates
      await new Promise((resolve) => requestAnimationFrame(resolve));
      await new Promise((resolve) => requestAnimationFrame(resolve));
      await new Promise((resolve) => requestAnimationFrame(resolve));

      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }

      // Preload images
      await Promise.all([
        preloadImage(`${publicUrl}/HeaderAce.png`),
        preloadImage(`${publicUrl}/FooterAce.png`),
        preloadImage(headerLogo)
      ]);
      await waitForRender(800);

      // Shared PDF Engine — capture + assembly (one A4 page per chunk)
      await generatePdfFromElementIds({
        elementIds: chunks.map((_, i) => `receipt-vouchers-pdf-page-${i}`),
        fileName: datedFileName(
          t('receiptVouchers.excel.fileName', { defaultValue: 'ReceiptVouchers' }),
        ),
        isRTL,
      });
      toast.success(t('receiptVouchers.print.success', { defaultValue: 'PDF downloaded successfully' }), {
        rtl: isRTL,
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } catch (error) {
      toast.error(error.message || t('receiptVouchers.print.error', { defaultValue: 'An error occurred while creating the PDF' }), {
        rtl: isRTL,
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } finally {
      setIsGeneratingPdf(false);
      setPdfType(null);
    }
  };

  // Handle export single receipt voucher to Excel
  const handleExportReceiptVoucherToExcel = (voucher) => {
    const projectName = voucher.project_name || (isRTL ? (voucher.project?.name_ar || voucher.project?.name) : (voucher.project?.name_en || voucher.project?.name_ar || voucher.project?.name)) || '—';
    const accountName = voucher.account
      ? `${voucher.account.code ? `${voucher.account.code} - ` : ''}${isRTL ? (voucher.account.name_ar || voucher.account.name) : (voucher.account.name || voucher.account.name_ar)}`
      : '—';
    const totalAmount = Number(voucher.total_amount || voucher.amount || 0);

    const data = [{
      [t('common.serialNumber', { defaultValue: '#' })]: voucher._serialNumber || 1,
      [t('receiptVouchers.tableHeaders.date', { defaultValue: 'Date' })]: formatDate(voucher.voucher_date || voucher.date) || '—',
      [t('receiptVouchers.tableHeaders.reference', { defaultValue: 'Reference' })]: voucher.reference || voucher.voucher_number || voucher.id || '—',
      [t('receiptVouchers.tableHeaders.receivedFromName', { defaultValue: 'Received From' })]: voucher.received_from_name || '—',
      [t('receiptVouchers.tableHeaders.project', { defaultValue: 'المشروع' })]: projectName,
      [t('receiptVouchers.tableHeaders.account', { defaultValue: 'Account' })]: accountName,
      [t('receiptVouchers.tableHeaders.amount', { defaultValue: 'Amount' })]: totalAmount,
      [t('receiptVouchers.tableHeaders.status', { defaultValue: 'Status' })]: statusLabel(voucher.status),
      [t('receiptVouchers.tableHeaders.createdBy', { defaultValue: 'Created By' })]: voucher.created_by || voucher.creator || '—',
      [t('receiptVouchers.tableHeaders.postedDate', { defaultValue: 'Posted Date' })]: formatDate(voucher.posted_at) || '—',
    }];

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, isRTL ? 'سند قبض' : 'Receipt Voucher');
    const identifier = voucher.voucher_number || voucher.reference || voucher.id;
    const fileName = `${isRTL ? 'سند_قبض' : 'ReceiptVoucher'}_${identifier}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success(t('receiptVouchers.export.success', { defaultValue: 'تم تصدير البيانات بنجاح' }), { rtl: isRTL });
  };

  // Handle export all receipt vouchers to Excel
  const handleExportAllToExcel = async () => {
    try {
      const allVouchers = await fetchAllReceiptVouchersForPDF();
      if (!allVouchers || allVouchers.length === 0) {
        toast.error(t('receiptVouchers.export.noData', { defaultValue: 'No data available' }), {
          rtl: isRTL,
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
        return;
      }

      const sortedAllVouchers = sortVouchersList(allVouchers);
      const data = sortedAllVouchers.map((voucher) => {
        const projectName = voucher.project_name || (isRTL ? (voucher.project?.name_ar || voucher.project?.name) : (voucher.project?.name_en || voucher.project?.name_ar || voucher.project?.name)) || '—';
        const accountName = voucher.account
          ? `${voucher.account.code ? `${voucher.account.code} - ` : ''}${isRTL ? (voucher.account.name_ar || voucher.account.name) : (voucher.account.name || voucher.account.name_ar)}`
          : '—';
        const totalAmount = Number(voucher.total_amount || voucher.amount || 0);

        return {
          [t('common.serialNumber', { defaultValue: '#' })]: voucher._serialNumber,
          [t('receiptVouchers.tableHeaders.date', { defaultValue: 'Date' })]: formatDate(voucher.voucher_date || voucher.date) || '—',
          [t('receiptVouchers.tableHeaders.reference', { defaultValue: 'Reference' })]: voucher.reference || voucher.voucher_number || voucher.id || '—',
          [t('receiptVouchers.tableHeaders.receivedFromName', { defaultValue: 'Received From' })]: voucher.received_from_name || '—',
          [t('receiptVouchers.tableHeaders.project', { defaultValue: 'المشروع' })]: projectName,
          [t('receiptVouchers.tableHeaders.account', { defaultValue: 'Account' })]: accountName,
          [t('receiptVouchers.tableHeaders.amount', { defaultValue: 'Amount' })]: totalAmount,
          [t('receiptVouchers.tableHeaders.status', { defaultValue: 'Status' })]: statusLabel(voucher.status),
          [t('receiptVouchers.tableHeaders.createdBy', { defaultValue: 'Created By' })]: voucher.created_by || voucher.creator || '—',
          [t('receiptVouchers.tableHeaders.postedDate', { defaultValue: 'Posted Date' })]: formatDate(voucher.posted_at) || '—',
        };
      });

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, t('receiptVouchers.excel.sheetName', { defaultValue: 'Receipt Vouchers' }));
      const fileName = `${t('receiptVouchers.excel.fileName', { defaultValue: 'ReceiptVouchers' })}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.success(t('receiptVouchers.export.success', { defaultValue: 'Data exported successfully' }), {
        rtl: isRTL,
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } catch (err) {
      toast.error(t('receiptVouchers.export.error', { defaultValue: 'Failed to export data' }), {
        rtl: isRTL,
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    }
  };

  const statusLabel = (status) => {
    return t(`receiptVouchers.status.${status || 'draft'}`, { defaultValue: status || '—' });
  };

  const statusColor = (status) => {
    switch (status) {
      case 'posted':
        return '#AC2000';
      case 'draft':
        return '#666';
      default:
        return '#0d6efd';
    }
  };

  const renderPagination = () => {
    if (totalPages === 0) {
      return null;
    }

    const current = currentPageSafe;
    const last = totalPages;

    return (
      <div
        style={{
          padding: '20px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '10px',
          borderTop: '1px solid #333',
          flexDirection: isRTL ? 'row-reverse' : 'row'
        }}
      >
        {current > 1 && (
          <button
            onClick={() => setCurrentPage(1)}
            style={{
              backgroundColor: '#1b3350',
              color: 'white',
              border: '1px solid #333',
              padding: '8px 12px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
            title={t('common.firstPage')}
          >
            {isRTL ? '>>' : '<<'}
          </button>
        )}
        {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
          let page;
          if (totalPages <= 10) {
            page = i + 1;
          } else if (current <= 5) {
            page = i + 1;
          } else if (current >= totalPages - 4) {
            page = totalPages - 9 + i;
          } else {
            page = current - 4 + i;
          }
          return page;
        }).map((page) => (
          <button
            key={page}
            onClick={() => setCurrentPage(page)}
            style={{
              backgroundColor: page === current ? '#AC2000' : '#1b3350',
              color: 'white',
              border: page === current ? 'none' : '1px solid #333',
              padding: '8px 12px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: page === current ? 'bold' : 'normal',
            }}
          >
            {page}
          </button>
        ))}
        {current < last && (
          <button
            onClick={() => setCurrentPage(last)}
            style={{
              backgroundColor: '#1b3350',
              color: 'white',
              border: '1px solid #333',
              padding: '8px 12px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
            title={t('common.lastPage')}
          >
            {isRTL ? '<<' : '>>'}
          </button>
        )}
        <span style={{ color: '#9ca3af', fontSize: '14px', marginLeft: '10px' }}>
          {t('common.pageInfo', {
            current: currentPageSafe,
            total: totalPages
          })}
        </span>
      </div>
    );
  };

  if (isLoading && (!allReceiptVouchers || allReceiptVouchers.length === 0)) {
    return (
      <div
        style={{
          direction: dir,
          padding: '30px',
          backgroundColor: '#14263b',
          minHeight: 'calc(100vh - 80px)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {t('common.loading', { defaultValue: 'Loading...' })}
      </div>
    );
  }

  return (
    <>
      <div style={{ padding: '30px', backgroundColor: '#14263b', minHeight: 'calc(100vh - 80px)', color: 'white', direction: dir }}>
        <h1 style={{ fontSize: '20px', margin: '10px 0', textAlign }}>{t('receiptVouchers.title', { defaultValue: 'Receipt Vouchers' })}</h1>

        <div style={{ marginBottom: '16px', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('common.search', { defaultValue: 'بحث' })}
            style={{
              minWidth: '180px',
              padding: '10px',
              borderRadius: '8px',
              border: '1px solid #333',
              backgroundColor: '#1a1f2e',
              color: 'white',
            }}
          />
          <div style={{ minWidth: '260px', position: 'relative' }} data-dropdown-container>
            <button
              type="button"
              onClick={() => setProjectDropdownOpen((prev) => !prev)}
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '8px',
                border: '1px solid #333',
                backgroundColor: '#1a1f2e',
                color: 'white',
                textAlign,
                cursor: 'pointer',
              }}
            >
              {renderProjectDisplay(projectId)}
            </button>
            {projectDropdownOpen && (
              <DropdownWithSearch
                isOpen={projectDropdownOpen}
                onToggle={setProjectDropdownOpen}
                searchTerm={projectSearchTerm}
                onSearchChange={handleProjectSearchChange}
                searchInputRef={projectSearchInputRef}
                items={projects || []}
                pagination={projectsPagination}
                currentPage={projectCurrentPage}
                onPageChange={setProjectCurrentPage}
                onSelect={(id) => {
                  setProjectId(String(id));
                  setProjectDropdownOpen(false);
                }}
                label={t('receiptVouchers.form.project', { defaultValue: 'المشروع' })}
              />
            )}
          </div>
          <div style={{ minWidth: '300px', position: 'relative' }} data-dropdown-container>
            <button
              type="button"
              onClick={() => setAccountDropdownOpen((prev) => !prev)}
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '8px',
                border: '1px solid #333',
                backgroundColor: '#1a1f2e',
                color: 'white',
                textAlign,
                cursor: 'pointer',
              }}
            >
              {renderAccountDisplay(accountId)}
            </button>
            {accountDropdownOpen && (
              <div style={{ position: 'absolute', top: '100%', marginTop: '6px', width: '100%', backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px', zIndex: 30, maxHeight: '320px', overflow: 'auto' }}>
                <input
                  type="text"
                  value={accountSearchTerm}
                  onChange={(e) => setAccountSearchTerm(e.target.value)}
                  placeholder={t('paymentVouchers.form.searchAccount')}
                  style={{ width: '100%', padding: '10px', border: 'none', borderBottom: '1px solid #374151', backgroundColor: '#111827', color: 'white' }}
                />
                {filteredAccounts.map((acc) => {
                  const id = acc?.id ?? acc?.account_id;
                  return (
                    <div
                      key={id}
                      onClick={() => {
                        setAccountId(String(id));
                        setAccountDropdownOpen(false);
                      }}
                      style={{ padding: '10px', cursor: 'pointer', borderBottom: '1px solid #2b3544' }}
                    >
                      {`${acc.code ? `${acc.code} - ` : ''}${isRTL ? (acc.name_ar || acc.name || acc.name_en || '') : (acc.name_en || acc.name || acc.name_ar || '')}`}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              minWidth: '140px',
              padding: '10px',
              borderRadius: '8px',
              border: '1px solid #333',
              backgroundColor: '#1a1f2e',
              color: 'white',
            }}
          >
            <option value="">{t('common.all', { defaultValue: 'All' })}</option>
            <option value={STATUS_FILTER_ENUM.POSTED}>{t('receiptVouchers.status.posted', { defaultValue: 'Posted' })}</option>
            <option value={STATUS_FILTER_ENUM.DRAFT}>{t('receiptVouchers.status.draft', { defaultValue: 'Draft' })}</option>
          </select>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            style={{
              minWidth: '150px',
              padding: '10px',
              borderRadius: '8px',
              border: '1px solid #333',
              backgroundColor: '#1a1f2e',
              color: 'white',
            }}
          />
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            style={{
              minWidth: '150px',
              padding: '10px',
              borderRadius: '8px',
              border: '1px solid #333',
              backgroundColor: '#1a1f2e',
              color: 'white',
            }}
          />
          <button
            onClick={applyFilters}
            style={{
              backgroundColor: '#AC2000',
              border: 'none',
              color: 'white',
              padding: '10px 14px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 'bold',
            }}
          >
            {t('common.apply', { defaultValue: 'Apply' })}
          </button>
          <button
            onClick={resetFilters}
            style={{
              backgroundColor: '#374151',
              border: 'none',
              color: 'white',
              padding: '10px 14px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 'bold',
            }}
          >
            {t('common.reset', { defaultValue: 'Reset' })}
          </button>
        </div>

        <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              onClick={handlePreviewAll}
              disabled={!filteredReceiptVouchers || filteredReceiptVouchers.length === 0}
              style={{
                backgroundColor: !filteredReceiptVouchers || filteredReceiptVouchers.length === 0 ? '#666' : '#444',
                border: 'none',
                color: 'white',
                padding: '10px 14px',
                borderRadius: '8px',
                cursor: !filteredReceiptVouchers || filteredReceiptVouchers.length === 0 ? 'not-allowed' : 'pointer',
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
                flexDirection: isRTL ? 'row' : 'row-reverse'
              }}
            >
              <FaEye /> {t('receiptVouchers.preview.all', { defaultValue: 'Preview PDF All' })}
            </button>
            <button
              onClick={handlePrintAll}
              disabled={isGeneratingPdf || !filteredReceiptVouchers || filteredReceiptVouchers.length === 0}
              style={{
                backgroundColor: isGeneratingPdf || !filteredReceiptVouchers || filteredReceiptVouchers.length === 0 ? '#666' : '#1f5ff1',
                border: 'none',
                color: 'white',
                padding: '10px 14px',
                borderRadius: '8px',
                cursor: isGeneratingPdf || !filteredReceiptVouchers || filteredReceiptVouchers.length === 0 ? 'not-allowed' : 'pointer',
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
                flexDirection: isRTL ? 'row' : 'row-reverse',
                opacity: isGeneratingPdf ? 0.6 : 1
              }}
            >
              <FaPrint /> {isGeneratingPdf ? t('receiptVouchers.print.generating', { defaultValue: 'Generating...' }) : t('receiptVouchers.print.all', { defaultValue: 'Print PDF All' })}
            </button>
            <button
              onClick={handleExportAllToExcel}
              disabled={!filteredReceiptVouchers || filteredReceiptVouchers.length === 0}
              style={{
                backgroundColor: !filteredReceiptVouchers || filteredReceiptVouchers.length === 0 ? '#666' : '#27ae60',
                border: 'none',
                color: 'white',
                padding: '10px 14px',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
              }}
            >
              <FaFileExcel /> {t('receiptVouchers.export.all', { defaultValue: 'Export Excel' })}
            </button>
            <button
              onClick={() => navigate('/receipt-vouchers/add')}
              style={{
                backgroundColor: '#AC2000',
                border: 'none',
                color: 'white',
                padding: '10px 14px',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
                flexDirection: isRTL ? 'row' : 'row-reverse'
              }}
            >
              <FaPlus /> {t('receiptVouchers.add.title', { defaultValue: 'Add Receipt Voucher' })}
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <label style={{ color: 'white', fontSize: '14px', whiteSpace: 'nowrap' }}>
                {t('common.perPage', { defaultValue: 'عدد السجلات' })}:
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <button
                  onClick={() => setPerPageInput(Math.max(1, perPageInput - 1))}
                  style={{
                    backgroundColor: '#1b3350',
                    color: 'white',
                    border: '1px solid #333',
                    padding: '6px 10px',
                    borderRadius: '6px 0 0 6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '32px'
                  }}
                >
                  −
                </button>
                <input
                  type="number"
                  min="1"
                  max="1000"
                  value={perPageInput}
                  onChange={(e) => {
                    const value = parseInt(e.target.value) || 1;
                    setPerPageInput(Math.max(1, Math.min(value, 1000)));
                  }}
                  style={{
                    width: '70px',
                    padding: '6px 10px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderLeft: 'none',
                    borderRight: 'none',
                    borderRadius: 0,
                    color: 'white',
                    fontSize: '14px',
                    textAlign: 'center'
                  }}
                />
                <button
                  onClick={() => setPerPageInput(Math.min(1000, perPageInput + 1))}
                  style={{
                    backgroundColor: '#1b3350',
                    color: 'white',
                    border: '1px solid #333',
                    padding: '6px 10px',
                    borderRadius: '0 6px 6px 0',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '32px'
                  }}
                >
                  +
                </button>
              </div>
              <button
                onClick={handleApplyPerPage}
                style={{
                  backgroundColor: '#AC2000',
                  color: 'white',
                  border: 'none',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  whiteSpace: 'nowrap'
                }}
              >
                {t('common.apply', { defaultValue: 'تطبيق' })}
              </button>
            </div>
          </div>
        </div>

        <div style={{ backgroundColor: '#1b3350', border: '1px solid #333', borderRadius: '12px', overflow: 'hidden', direction: dir }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, direction: dir }}>
              <thead>
                <tr style={{ backgroundColor: '#AC2000' }}>
                  {[
                    { label: t('common.serialNumber', { defaultValue: '#' }), width: '60px', field: 'id' },
                    { label: t('receiptVouchers.tableHeaders.date', { defaultValue: 'Date' }), width: '120px', field: 'date' },
                    { label: t('receiptVouchers.tableHeaders.reference', { defaultValue: 'Reference' }), width: '150px', field: 'reference' },
                    { label: t('receiptVouchers.tableHeaders.receivedFromName', { defaultValue: 'Received From' }), width: '180px', field: 'received_from_name' },
                    { label: t('receiptVouchers.tableHeaders.project', { defaultValue: 'المشروع' }), width: '180px', field: 'project' },
                    { label: t('receiptVouchers.tableHeaders.account', { defaultValue: 'Account' }), width: '220px', field: 'account' },
                    { label: t('receiptVouchers.tableHeaders.amount', { defaultValue: 'Amount' }), width: '140px', field: 'amount' },
                    { label: t('receiptVouchers.tableHeaders.status', { defaultValue: 'Status' }), width: '120px', field: 'status' },
                    { label: t('receiptVouchers.tableHeaders.createdBy', { defaultValue: 'Created By' }), width: '140px', field: 'created_by' },
                    { label: t('receiptVouchers.tableHeaders.postedDate', { defaultValue: 'Posted Date' }), width: '160px', field: 'posted_date' },
                    { label: t('receiptVouchers.tableHeaders.actions', { defaultValue: 'Actions' }), width: '220px' },
                  ].map((h) => (
                    <th
                      key={h.label}
                      onClick={() => h.field && handleSort(h.field)}
                      style={{
                        padding: '18px 16px',
                        color: 'white',
                        borderBottom: '2px solid #333',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        width: h.width,
                        minWidth: h.width,
                        backgroundColor: '#AC2000',
                        cursor: h.field ? 'pointer' : 'default',
                        userSelect: 'none',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                        <span>{h.label}</span>
                        {h.field && sortField === h.field && (
                          sortOrder === 'asc'
                            ? <FaSortUp style={{ color: '#ffd700', verticalAlign: 'middle' }} />
                            : <FaSortDown style={{ color: '#ffd700', verticalAlign: 'middle' }} />
                        )}
                        {h.field && sortField !== h.field && (
                          <FaSort style={{ color: '#ccc', verticalAlign: 'middle', opacity: 0.7 }} />
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedReceiptVouchers.length === 0 ? (
                  <tr>
                    <td
                      colSpan="11"
                      style={{
                        padding: '40px',
                        textAlign: 'center',
                        color: '#9ca3af',
                        fontSize: '16px',
                        direction: dir
                      }}
                    >
                      {t('receiptVouchers.noData', { defaultValue: 'No receipt vouchers found' })}
                    </td>
                  </tr>
                ) : (
                  paginatedReceiptVouchers.map((voucher, idx) => {
                    const isDraft = voucher.status === 'draft';
                    const isPosted = voucher.status === 'posted';
                    const totalAmount = Number(voucher.total_amount || 0);

                    return (
                      <tr
                        key={voucher.id}
                        style={{
                          borderBottom: '1px solid #333',
                          backgroundColor: idx % 2 === 0 ? '#1b3350' : '#1a1f2e',
                          transition: 'background-color 0.2s',
                        }}
                        onMouseEnter={(ev) => {
                          const row = ev.target.closest('tr');
                          if (row) row.style.backgroundColor = '#2a3441';
                        }}
                        onMouseLeave={(ev) => {
                          const row = ev.target.closest('tr');
                          if (row) row.style.backgroundColor = idx % 2 === 0 ? '#1b3350' : '#1a1f2e';
                        }}
                      >
                        <td style={{ padding: '18px 16px', textAlign: 'center', color: 'white', fontSize: '14px' }}>
                          {voucher._serialNumber}
                        </td>
                        <td style={{ padding: '18px 16px', textAlign: 'center', color: 'white', fontSize: '14px' }}>
                          {formatDate(voucher.voucher_date || voucher.date)}
                        </td>
                        <td style={{ padding: '18px 16px', textAlign: 'center', color: 'white', fontSize: '14px', direction: dir }}>
                          {voucher.reference || '—'}
                        </td>
                        <td style={{ padding: '18px 16px', textAlign: 'center', color: 'white', fontSize: '14px', direction: dir }}>
                          {voucher.received_from_name || '—'}
                        </td>
                        <td style={{ padding: '18px 16px', textAlign: 'center', color: 'white', fontSize: '14px', direction: dir }}>
                          {voucher.project_name || (isRTL ? (voucher.project?.name_ar || voucher.project?.name) : (voucher.project?.name_en || voucher.project?.name_ar || voucher.project?.name)) || '—'}
                        </td>
                        <td style={{ padding: '18px 16px', textAlign: 'center', color: 'white', fontSize: '14px', direction: dir }}>
                          {voucher.account
                            ? `${voucher.account.code ? `${voucher.account.code} - ` : ''}${isRTL ? (voucher.account.name_ar || voucher.account.name) : (voucher.account.name || voucher.account.name_ar)}`
                            : '—'}
                        </td>
                        <td
                          style={{
                            padding: '18px 16px',
                            textAlign: 'center',
                            color: 'white',
                            fontSize: '14px',
                            fontWeight: '600',
                          }}
                        >
                          {convertArabicNumbersToEnglish(totalAmount.toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }))}
                        </td>
                        <td style={{ padding: '18px 16px', textAlign: 'center' }}>
                          <span
                            style={{
                              backgroundColor: statusColor(voucher.status),
                              color: 'white',
                              padding: '6px 14px',
                              borderRadius: '20px',
                              fontSize: '12px',
                              fontWeight: 'bold',
                              display: 'inline-block',
                              direction: dir
                            }}
                          >
                            {statusLabel(voucher.status)}
                          </span>
                        </td>
                        <td style={{ padding: '18px 16px', textAlign: 'center', color: 'white', fontSize: '14px', direction: dir }}>
                          {voucher.created_by || voucher.creator || '—'}
                        </td>
                        <td style={{ padding: '18px 16px', textAlign: 'center', color: 'white', fontSize: '14px' }}>
                          {formatDate(voucher.posted_at)}
                        </td>
                        <td style={{ padding: '18px 16px', textAlign: 'center' }}>
                          <div
                            style={{
                              display: 'flex',
                              gap: '8px',
                              justifyContent: 'center',
                              alignItems: 'center',
                              flexWrap: 'nowrap',
                            }}
                          >
                            {/* Attachments icon (shown only when there are attachments) */}
                            {voucher.attachments && voucher.attachments.length > 0 && (
                              <button
                                title={t('receiptVouchers.actions.attachments', { defaultValue: 'Attachments' })}
                                onClick={() => openAttachmentsModal(voucher.attachments)}
                                style={{
                                  backgroundColor: '#6c757d',
                                  color: 'white',
                                  border: 'none',
                                  padding: '10px 12px',
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  fontSize: '14px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.2s',
                                }}
                                onMouseEnter={(ev) => {
                                  ev.target.style.backgroundColor = '#868e96';
                                  ev.target.style.transform = 'scale(1.05)';
                                }}
                                onMouseLeave={(ev) => {
                                  ev.target.style.backgroundColor = '#6c757d';
                                  ev.target.style.transform = 'scale(1)';
                                }}
                              >
                                <FaPaperclip />
                              </button>
                            )}
                            <button
                              title={t('receiptVouchers.actions.view', { defaultValue: 'View Details' })}
                              onClick={() => handlePreviewSingle(voucher)}
                              disabled={isLoadingDetails}
                              style={{
                                backgroundColor: isLoadingDetails ? '#555' : '#666',
                                color: 'white',
                                border: 'none',
                                padding: '10px 12px',
                                borderRadius: '8px',
                                cursor: isLoadingDetails ? 'not-allowed' : 'pointer',
                                fontSize: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s',
                                opacity: isLoadingDetails ? 0.6 : 1,
                              }}
                              onMouseEnter={(ev) => {
                                if (!isLoadingDetails) {
                                  ev.target.style.backgroundColor = '#777';
                                  ev.target.style.transform = 'scale(1.05)';
                                }
                              }}
                              onMouseLeave={(ev) => {
                                if (!isLoadingDetails) {
                                  ev.target.style.backgroundColor = '#666';
                                  ev.target.style.transform = 'scale(1)';
                                }
                              }}
                            >
                              <FaEye />
                            </button>
                            <button
                              title={t('receiptVouchers.actions.print', { defaultValue: 'Print' })}
                              onClick={() => handlePrintReceiptVoucher(voucher)}
                              disabled={isGeneratingPdf}
                              style={{
                                backgroundColor: isGeneratingPdf ? '#666' : '#1f5ff1',
                                color: 'white',
                                border: 'none',
                                padding: '10px 12px',
                                borderRadius: '8px',
                                cursor: isGeneratingPdf ? 'not-allowed' : 'pointer',
                                fontSize: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s',
                                opacity: isGeneratingPdf ? 0.6 : 1,
                              }}
                              onMouseEnter={(ev) => {
                                if (!isGeneratingPdf) {
                                  ev.target.style.backgroundColor = '#138496';
                                  ev.target.style.transform = 'scale(1.05)';
                                }
                              }}
                              onMouseLeave={(ev) => {
                                if (!isGeneratingPdf) {
                                  ev.target.style.backgroundColor = '#17a2b8';
                                  ev.target.style.transform = 'scale(1)';
                                }
                              }}
                            >
                              <FaPrint />
                            </button>
                            <button
                              title={t('receiptVouchers.actions.export', { defaultValue: 'Export Excel' })}
                              onClick={() => handleExportReceiptVoucherToExcel(voucher)}
                              style={{
                                backgroundColor: '#27ae60',
                                color: 'white',
                                border: 'none',
                                padding: '10px 12px',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s',
                              }}
                              onMouseEnter={(ev) => {
                                ev.target.style.backgroundColor = '#218838';
                                ev.target.style.transform = 'scale(1.05)';
                              }}
                              onMouseLeave={(ev) => {
                                ev.target.style.backgroundColor = '#27ae60';
                                ev.target.style.transform = 'scale(1)';
                              }}
                            >
                              <FaFileExcel />
                            </button>

                            {isDraft && (
                              <>
                                <button
                                  title={t('receiptVouchers.actions.edit', { defaultValue: 'Edit' })}
                                  onClick={() => navigate(`/receipt-vouchers/edit/${voucher.id}`)}
                                  style={{
                                    backgroundColor: '#f59e42',
                                    color: 'white',
                                    border: 'none',
                                    padding: '10px 12px',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.2s',
                                  }}
                                  onMouseEnter={(ev) => {
                                    ev.target.style.backgroundColor = '#e67e22';
                                    ev.target.style.transform = 'scale(1.05)';
                                  }}
                                  onMouseLeave={(ev) => {
                                    ev.target.style.backgroundColor = '#f59e42';
                                    ev.target.style.transform = 'scale(1)';
                                  }}
                                >
                                  <FaEdit />
                                </button>
                                <button
                                  title={t('receiptVouchers.actions.post', { defaultValue: 'Post' })}
                                  onClick={() => handlePost(voucher.id)}
                                  style={{
                                    backgroundColor: '#845ef7',
                                    color: 'white',
                                    border: 'none',
                                    padding: '10px 12px',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.2s',
                                  }}
                                  onMouseEnter={(ev) => {
                                    ev.target.style.backgroundColor = '#7950f2';
                                    ev.target.style.transform = 'scale(1.05)';
                                  }}
                                  onMouseLeave={(ev) => {
                                    ev.target.style.backgroundColor = '#845ef7';
                                    ev.target.style.transform = 'scale(1)';
                                  }}
                                >
                                  <FaPaperPlane />
                                </button>
                                <button
                                  title={t('receiptVouchers.actions.delete', { defaultValue: 'Delete' })}
                                  onClick={() => openDeleteModal(voucher)}
                                  style={{
                                    backgroundColor: '#dc3545',
                                    color: 'white',
                                    border: 'none',
                                    padding: '10px 12px',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.2s',
                                  }}
                                  onMouseEnter={(ev) => {
                                    ev.target.style.backgroundColor = '#bb2d3b';
                                    ev.target.style.transform = 'scale(1.05)';
                                  }}
                                  onMouseLeave={(ev) => {
                                    ev.target.style.backgroundColor = '#dc3545';
                                    ev.target.style.transform = 'scale(1)';
                                  }}
                                >
                                  <FaTrash />
                                </button>
                              </>
                            )}

                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {renderPagination()}
        </div>

        {showDeleteModal && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 999999,
            }}
          >
            <div
              style={{
                backgroundColor: '#1f2937',
                border: '1px solid #333',
                borderRadius: '12px',
                padding: '24px',
                width: '90%',
                maxWidth: '420px',
                direction: dir
              }}
            >
              <h3 style={{ color: 'white', marginBottom: '12px', fontSize: '18px', textAlign }}>{t('receiptVouchers.delete.title', { defaultValue: 'Confirm Delete' })}</h3>
              <p style={{ color: '#ccc', marginBottom: '8px', fontSize: '14px', textAlign }}>
                {t('receiptVouchers.delete.confirm', { defaultValue: 'Are you sure you want to delete this receipt voucher?' })}
              </p>
              {voucherToDelete && (
                <div style={{ color: 'white', fontSize: '14px', marginBottom: '16px', textAlign, direction: dir }}>
                  {t('receiptVouchers.tableHeaders.reference', { defaultValue: 'Reference' })}: {voucherToDelete.reference || '—'}
                </div>
              )}
              <p style={{ color: '#ef4444', marginBottom: '20px', fontSize: '12px', textAlign }}>{t('receiptVouchers.delete.irreversible', { defaultValue: 'This action cannot be undone.' })}</p>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                <button
                  onClick={closeDeleteModal}
                  style={{ backgroundColor: '#666', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 16px', cursor: 'pointer' }}
                >
                  {t('receiptVouchers.delete.cancel', { defaultValue: 'Cancel' })}
                </button>
                <button
                  onClick={confirmDelete}
                  style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 16px', cursor: 'pointer' }}
                >
                  {t('receiptVouchers.delete.confirmButton', { defaultValue: 'Delete' })}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Attachments Modal (using portal, similar to ProgressBill) */}
        {attachmentsModalOpen && ReactDOM.createPortal(
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.7)',
              zIndex: 999999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 16,
            }}
            onClick={closeAttachmentsModal}
          >
            <div
              style={{
                backgroundColor: '#1f2937',
                border: '1px solid #333',
                borderRadius: 12,
                padding: 20,
                width: '95%',
                maxWidth: 900,
                maxHeight: '90vh',
                overflow: 'auto',
                margin: 'auto',
                direction: dir,
                textAlign,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 style={{ marginTop: 0, marginBottom: 16, color: 'white' }}>
                {t('receiptVouchers.attachmentsModal.title', { defaultValue: 'المرفقات' })}
              </h3>

              {selectedAttachments && Array.isArray(selectedAttachments) && selectedAttachments.length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16 }}>
                  {selectedAttachments.map((attachment, idx) => {
                    const isImage =
                      attachment.mime_type?.startsWith('image/') ||
                      /\.(png|jpe?g|gif|webp)$/i.test(attachment.file_name || '');

                    const fileUrl =
                      attachment?.file_url ||
                      attachment?.url ||
                      attachment?.media_url ||
                      (attachment?.path ? `${BASE_URL.slice(0, -3)}/storage/${attachment.path}` : '') ||
                      attachment?.full_url ||
                      attachment?.link ||
                      '';

                    return (
                      <div
                        key={attachment.id || idx}
                        style={{
                          backgroundColor: '#1a1f2e',
                          border: '1px solid #333',
                          borderRadius: 8,
                          padding: 12,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 8,
                        }}
                      >
                        {isImage && fileUrl ? (
                          <div
                            style={{
                              position: 'relative',
                              width: '100%',
                              paddingTop: '75%',
                              backgroundColor: '#14263b',
                              borderRadius: 4,
                              overflow: 'hidden',
                            }}
                          >
                            <img
                              src={attachment.path ? `${BASE_URL.slice(0, -3)}/storage/${attachment.path}` : fileUrl}
                              alt={attachment.file_name || `Attachment ${idx + 1}`}
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                cursor: 'pointer',
                              }}
                              onClick={() => fileUrl && window.open(fileUrl, '_blank')}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                const errorDiv = e.target.nextSibling;
                                if (errorDiv) errorDiv.style.display = 'flex';
                              }}
                            />
                            <div
                              style={{
                                display: 'none',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#888',
                                fontSize: 12,
                              }}
                            >
                              {t('receiptVouchers.attachmentsModal.imageError', { defaultValue: 'خطأ في تحميل الصورة' })}
                            </div>
                          </div>
                        ) : (
                          <div
                            style={{
                              width: '100%',
                              paddingTop: '75%',
                              backgroundColor: '#14263b',
                              borderRadius: 4,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#888',
                            }}
                          >
                            <FaFileAlt style={{ fontSize: 32 }} />
                          </div>
                        )}
                        <div
                          style={{
                            fontSize: 12,
                            color: '#ccc',
                            wordBreak: 'break-word',
                            textAlign: 'center',
                          }}
                        >
                          {attachment.file_name || attachment.name || `Attachment ${idx + 1}`}
                        </div>
                        {fileUrl && (
                          <a
                            href={fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              backgroundColor: '#AC2000',
                              color: 'white',
                              border: 'none',
                              padding: '8px 12px',
                              borderRadius: 6,
                              cursor: 'pointer',
                              textAlign: 'center',
                              textDecoration: 'none',
                              fontSize: 12,
                              fontWeight: 'bold',
                            }}
                          >
                            {isImage
                              ? t('receiptVouchers.attachmentsModal.view', { defaultValue: 'عرض' })
                              : t('receiptVouchers.attachmentsModal.download', { defaultValue: 'تحميل' })}
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: 40, color: '#ccc' }}>
                  {t('receiptVouchers.attachmentsModal.noAttachments', { defaultValue: 'لا توجد مرفقات' })}
                </div>
              )}

              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 24 }}>
                <button
                  onClick={closeAttachmentsModal}
                  style={{
                    backgroundColor: '#4b5563',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: 14,
                  }}
                >
                  {t('receiptVouchers.attachmentsModal.close', { defaultValue: 'إغلاق' })}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Hidden PDF Templates */}
        {voucherForPrint && pdfType === 'single' && (
          <ReceiptVoucherSingleTemplate
            voucher={voucherForPrint}
            isRTL={isRTL}
            formatDate={formatDate}
            formatCurrency={formatCurrency}
            statusLabel={statusLabel}
            publicUrl={publicUrl}
            headerLogo={headerLogo}
            isPreview={false}
            t={t}
          />
        )}

        {/* Dynamic measurement template for page splitting */}
        <ReceiptVouchersPdfMeasurementTemplate
          receiptVouchers={allVouchersForPDF || []}
          isRTL={isRTL}
          t={t}
          formatDate={formatDate}
          formatCurrency={formatCurrency}
          statusLabel={statusLabel}
          activeFiltersText={activeFiltersForPdf}
          publicUrl={publicUrl}
        />

        {/* Print Templates for All Receipt Vouchers — rendered ONLY during PDF
            generation (rendering them alongside the preview doubles the DOM) */}
        {isGeneratingPdf && (pdfChunks || []).map((chunk, chunkIndex) => (
          <ReceiptVouchersPdfPageTemplate
            key={chunkIndex}
            idPrefix="receipt-vouchers-pdf-page"
            chunkIndex={chunkIndex}
            receiptVouchers={chunk.items}
            startIndex={chunk.startIndex}
            isRTL={isRTL}
            t={t}
            formatDate={formatDate}
            formatCurrency={formatCurrency}
            statusLabel={statusLabel}
            publicUrl={publicUrl}
            headerLogo={headerLogo}
            activeFiltersText={activeFiltersForPdf}
            isPreview={false}
            totalChunks={pdfChunks?.length || 1}
          />
        ))}

        {/* Preview Modal */}
        <PdfPreviewModal
          isOpen={isPreviewOpen && (voucherForPreview || (pdfChunks && pdfChunks.length > 0))}
          title={voucherForPreview ? t('receiptVouchers.preview.single', { defaultValue: 'Receipt Voucher Preview' }) : t('receiptVouchers.title', { defaultValue: 'Receipt Vouchers' })}
          onClose={() => {
            setIsPreviewOpen(false);
            setVoucherForPreview(null);
            setPdfChunks([]);
          }}
          onPrint={voucherForPreview ? () => {
            setVoucherForPrint(voucherForPreview);
            setPdfType('single');
            handlePrintReceiptVoucher(voucherForPreview);
          } : handlePrintAll}
          isGeneratingPdf={isGeneratingPdf}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
              alignItems: "center",
              width: "100%"
            }}
          >
            {voucherForPreview ? (
              <ReceiptVoucherSingleTemplate
                voucher={voucherForPreview}
                isRTL={isRTL}
                formatDate={formatDate}
                formatCurrency={formatCurrency}
                statusLabel={statusLabel}
                publicUrl={publicUrl}
                headerLogo={headerLogo}
                isPreview={true}
                t={t}
              />
            ) : (
              (pdfChunks || []).map((chunk, chunkIndex) => (
                <ReceiptVouchersPdfPageTemplate
                  key={chunkIndex}
                  idPrefix="preview-receipt-vouchers-pdf-page"
                  chunkIndex={chunkIndex}
                  receiptVouchers={chunk.items}
                  startIndex={chunk.startIndex}
                  isRTL={isRTL}
                  t={t}
                  formatDate={formatDate}
                  formatCurrency={formatCurrency}
                  statusLabel={statusLabel}
                  publicUrl={publicUrl}
                  headerLogo={headerLogo}
                  totalChunks={pdfChunks?.length || 1}
                  activeFiltersText={activeFiltersForPdf}
                  isPreview={true}
                />
              ))
            )}
          </div>
        </PdfPreviewModal>
      </div>
    </>
  );
};

const ReceiptVouchersPdfPageTemplate = ({ idPrefix, receiptVouchers, isRTL, t, formatDate, formatCurrency, statusLabel, publicUrl, headerLogo, chunkIndex = 0, totalChunks = 1, activeFiltersText = '', startIndex = 0, isPreview = true }) => {
  if (!receiptVouchers || receiptVouchers.length === 0) return null;

  // Always show numbers in English
  const formatCurrencyEnglish = (amount) => convertArabicNumbersToEnglish(formatCurrency(amount));
  const formatDateEnglish = (date) => convertArabicNumbersToEnglish(formatDate(date));

  return (
    <div
      id={`${idPrefix}-${chunkIndex}`}
      aria-hidden={!isPreview}
      style={{
        position: isPreview ? 'relative' : 'absolute',
        left: isPreview ? 'auto' : '-9999px',
        top: isPreview ? 'auto' : 0,
        width: '794px',
        minHeight: '1123px',
        height: 'auto',
        backgroundColor: '#ffffff',
        color: '#000000',
        fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
        fontWeight: 'bold',
        padding: '40px',
        boxSizing: 'border-box',
        margin: isPreview ? '0 auto' : 0,
        overflow: 'visible',
        marginBottom: isPreview && chunkIndex < totalChunks - 1 ? '20px' : 0,
        boxShadow: isPreview ? '0 4px 20px rgba(0, 0, 0, 0.15)' : 'none'
      }}
    >
      <div style={{
        border: '5px solid #000',
        padding: '3px',
        backgroundColor: '#ffffff',
        boxSizing: 'border-box',
        width: '100%',
        minHeight: '1043px',
        height: 'auto',
        overflow: 'visible'
      }}>
        <div style={{
          border: '2px solid #000',
          minHeight: '1033px',
          height: '1033px',
          padding: '18px 18px 10px',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
          fontWeight: 'bold',
          direction: isRTL ? 'rtl' : 'ltr',
          textAlign: isRTL ? 'right' : 'left',
          backgroundColor: '#ffffff',
          boxSizing: 'border-box',
          width: '100%',
          overflow: 'visible'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: `url(${headerLogo})`,
            backgroundSize: '95%',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.08,
            zIndex: 0,
            pointerEvents: 'none'
          }} />
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100%' }}>
            <img
              src={`${publicUrl}/HeaderAce.png`}
              alt="Header"
              style={{
                width: '100%',
                height: 'auto',
                objectFit: 'contain',
                marginBottom: '8px',
                borderBottom: '4px double #000',
                paddingBottom: '8px',
                flexShrink: 0
              }}
              crossOrigin="anonymous"
            />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '120px' }}>
              <div style={{ marginTop: '12px', textAlign: 'center', marginBottom: '12px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#000080', margin: 0 }}>
                  {t('receiptVouchers.title', { defaultValue: 'Receipt Vouchers' })}
                </h2>
                <p style={{ fontSize: '12px', color: '#333', margin: '4px 0 0' }}>
                  {new Date().toLocaleDateString(isRTL ? 'ar-SA' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
                {activeFiltersText && (
                  <div style={{ fontSize: '11px', color: '#444', margin: '4px 0 0', padding: '4px 8px', backgroundColor: 'rgba(0,0,128,0.05)', borderRadius: '4px', display: 'inline-block' }}>
                    {activeFiltersText}
                  </div>
                )}
              </div>
              <div style={{ direction: isRTL ? 'rtl' : 'ltr', fontFamily: '"Times New Roman", "Times", serif' }}>
                <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: '11px', border: '1px solid #000', fontFamily: '"Times New Roman", "Times", serif', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '4%', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('common.serialNumber', { defaultValue: '#' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '12%', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('receiptVouchers.tableHeaders.voucherNumber', { defaultValue: 'Voucher Number' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '10%', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('receiptVouchers.tableHeaders.date', { defaultValue: 'Date' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '15%', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('receiptVouchers.tableHeaders.receivedFromName', { defaultValue: 'Received From' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '15%', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('receiptVouchers.tableHeaders.project', { defaultValue: 'المشروع' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '28%', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('receiptVouchers.tableHeaders.description', { defaultValue: 'Description' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '10%', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('receiptVouchers.tableHeaders.amount', { defaultValue: 'Amount' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '6%', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('receiptVouchers.tableHeaders.status', { defaultValue: 'Status' })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {receiptVouchers.map((voucher, idx) => {
                      return (
                        <tr key={voucher.id || idx} style={{ backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle', backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{voucher._serialNumber}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle', backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{voucher.reference || voucher.voucher_number || voucher.id || '—'}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle', backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{formatDateEnglish(voucher.voucher_date || voucher.date) || '—'}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle', backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{voucher.received_from_name || '—'}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle', backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{voucher.project_name || (isRTL ? (voucher.project?.name_ar || voucher.project?.name) : (voucher.project?.name_en || voucher.project?.name_ar || voucher.project?.name)) || '—'}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left', verticalAlign: 'middle', wordBreak: 'break-word', backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{voucher.notes || (voucher.details && voucher.details.length > 0 && voucher.details[0].description) || '—'}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle', backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{formatCurrencyEnglish(voucher.total_amount || voucher.amount)}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle', backgroundColor: idx % 2 === 0 ? 'rgba(255, 255, 255, 0.7)' : 'rgba(255, 255, 255, 0.5)' }}>{statusLabel(voucher.status)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <div style={{
              position: 'absolute',
              bottom: '10px',
              left: '18px',
              right: '18px',
              flexShrink: 0
            }}>
              <img
                src={`${publicUrl}/FooterAce.png`}
                alt="Footer"
                style={{
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  borderTop: '4px double #000',
                  paddingTop: '8px',
                  flexShrink: 0
                }}
                crossOrigin="anonymous"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ReceiptVouchersPdfMeasurementTemplate = ({ receiptVouchers, isRTL, t, formatDate, formatCurrency, statusLabel, activeFiltersText = '', publicUrl = '' }) => {
  if (!receiptVouchers || receiptVouchers.length === 0) return null;

  const formatCurrencyEnglish = (amount) => convertArabicNumbersToEnglish(formatCurrency(amount));
  const formatDateEnglish = (date) => convertArabicNumbersToEnglish(formatDate(date));

  return (
    <div
      aria-hidden="true"
      style={{
        position: 'absolute',
        left: '-9999px',
        top: 0,
        width: '794px',
        minHeight: '1123px',
        backgroundColor: '#ffffff',
        color: '#000000',
        fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
        fontWeight: 'bold',
        padding: '40px',
        boxSizing: 'border-box',
        overflow: 'visible'
      }}
    >
      <div style={{ border: '5px solid #000', padding: '3px', backgroundColor: '#ffffff', boxSizing: 'border-box', width: '100%', minHeight: '1043px' }}>
        <div style={{ border: '2px solid #000', minHeight: '1033px', height: 'auto', padding: '18px 18px 10px', position: 'relative', display: 'flex', flexDirection: 'column', direction: isRTL ? 'rtl' : 'ltr', backgroundColor: '#ffffff' }}>
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100%' }}>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '0' }}>
              <div id="receipt-vouchers-pdf-measure-top-header" style={{ display: 'flex', flexDirection: 'column' }}>
                <img
                  src={`${publicUrl}/HeaderAce.png`}
                  alt="Header"
                  style={{
                    width: '100%',
                    height: 'auto',
                    objectFit: 'contain',
                    marginBottom: '8px',
                    borderBottom: '4px double #000',
                    paddingBottom: '8px',
                    flexShrink: 0
                  }}
                  crossOrigin="anonymous"
                />
                <div style={{ marginTop: '12px', textAlign: 'center', marginBottom: '12px' }}>
                  <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#000080', margin: 0 }}>
                    {t('receiptVouchers.title', { defaultValue: 'Receipt Vouchers' })}
                  </h2>
                  <p style={{ fontSize: '12px', color: '#333', margin: '4px 0 0' }}>
                    {new Date().toLocaleDateString(isRTL ? 'ar-SA' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                  {activeFiltersText && (
                    <div style={{ fontSize: '11px', color: '#444', margin: '4px 0 0', padding: '4px 8px', backgroundColor: 'rgba(0,0,128,0.05)', borderRadius: '4px', display: 'inline-block' }}>
                      {activeFiltersText}
                    </div>
                  )}
                </div>
              </div>
              <div style={{ direction: isRTL ? 'rtl' : 'ltr', fontFamily: '"Times New Roman", "Times", serif' }}>
                <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: '11px', border: '1px solid #000', fontFamily: '"Times New Roman", "Times", serif' }}>
                  <thead id="receipt-vouchers-pdf-measure-thead">
                    <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '4%' }}>{t('common.serialNumber', { defaultValue: '#' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '12%' }}>{t('receiptVouchers.tableHeaders.voucherNumber', { defaultValue: 'Voucher Number' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '10%' }}>{t('receiptVouchers.tableHeaders.date', { defaultValue: 'Date' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '15%' }}>{t('receiptVouchers.tableHeaders.receivedFromName', { defaultValue: 'Received From' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '15%' }}>{t('receiptVouchers.tableHeaders.project', { defaultValue: 'المشروع' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '28%' }}>{t('receiptVouchers.tableHeaders.description', { defaultValue: 'Description' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '10%' }}>{t('receiptVouchers.tableHeaders.amount', { defaultValue: 'Amount' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', width: '6%' }}>{t('receiptVouchers.tableHeaders.status', { defaultValue: 'Status' })}</th>
                    </tr>
                  </thead>
                  <tbody id="receipt-vouchers-pdf-measure-tbody">
                    {receiptVouchers.map((voucher, idx) => (
                      <tr key={voucher.id || idx}>
                        <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>{voucher._serialNumber}</td>
                        <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>{voucher.reference || voucher.voucher_number || voucher.id || '—'}</td>
                        <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>{formatDateEnglish(voucher.voucher_date || voucher.date) || '—'}</td>
                        <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>{voucher.received_from_name || '—'}</td>
                        <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>{voucher.project_name || (isRTL ? (voucher.project?.name_ar || voucher.project?.name) : (voucher.project?.name_en || voucher.project?.name_ar || voucher.project?.name)) || '—'}</td>
                        <td style={{ border: '1px solid #000', padding: '6px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left', verticalAlign: 'middle', wordBreak: 'break-word' }}>{voucher.notes || (voucher.details && voucher.details.length > 0 && voucher.details[0].description) || '—'}</td>
                        <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>{formatCurrencyEnglish(voucher.total_amount || voucher.amount)}</td>
                        <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', verticalAlign: 'middle' }}>{statusLabel(voucher.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {/* Footer reserve: fixed 120px placeholder — matches paddingBottom:'120px' in page template content area */}
            <div id="receipt-vouchers-pdf-measure-footer" style={{ height: '120px', flexShrink: 0 }} />
          </div>
        </div>
      </div>
    </div>
  );
};

// Receipt Voucher Single Template Component
// Receipt Voucher Single Template Component
const ReceiptVoucherSingleTemplate = ({ voucher, isRTL, formatDate, formatCurrency, statusLabel, publicUrl, headerLogo, isPreview = false, t }) => {
  if (!voucher) return null;

  // Always show numbers in English
  const formatCurrencyEnglish = (amount) => convertArabicNumbersToEnglish(formatCurrency(amount));
  const formatDateEnglish = (date) => convertArabicNumbersToEnglish(formatDate(date));

  const details = Array.isArray(voucher.details) ? voucher.details : [];
  const totalAmount = Number(voucher.total_amount || details.reduce((sum, line) => sum + (parseFloat(line.amount) || 0), 0));
  const accountName = voucher.account ? (isRTL ? (voucher.account.name_ar || voucher.account.name) : (voucher.account.name_en || voucher.account.name_ar || voucher.account.name)) : '';
  const accountCode = voucher.account?.code || '';

  return (
    <div
      id={isPreview ? `preview-receipt-voucher-pdf-${voucher.id}` : `receipt-voucher-pdf-${voucher.id}`}
      aria-hidden={!isPreview}
      style={{
        position: isPreview ? 'relative' : 'absolute',
        left: isPreview ? 'auto' : '-9999px',
        top: isPreview ? 'auto' : 0,
        width: '794px',
        minHeight: '1123px',
        backgroundColor: '#ffffff',
        color: '#000000',
        fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
        fontWeight: 'bold',
        padding: '40px',
        boxSizing: 'border-box',
        boxShadow: isPreview ? '0 4px 20px rgba(0, 0, 0, 0.15)' : 'none'
      }}
    >
      <div style={{
        border: '5px solid #000',
        padding: '3px',
        backgroundColor: '#ffffff',
        boxSizing: 'border-box',
        width: '100%',
        minHeight: '1043px',
      }}>
        <div style={{
          border: '2px solid #000',
          minHeight: '1033px',
          height: '1033px',
          padding: '18px 18px 10px',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
          fontWeight: 'bold',
          direction: isRTL ? 'rtl' : 'ltr',
          textAlign: isRTL ? 'right' : 'left',
          backgroundColor: '#ffffff',
        }}>
          {/* Header Internal Background */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: `url(${headerLogo})`,
            backgroundSize: '95%',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.08,
            zIndex: 0,
            pointerEvents: 'none'
          }} />

          {/* Main Content Container */}
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>

            {/* Header Image */}
            <img
              src={`${publicUrl}/HeaderAce.png`}
              alt="Header"
              style={{
                width: '100%',
                height: 'auto',
                objectFit: 'contain',
                marginBottom: '8px',
                borderBottom: '4px double #000',
                paddingBottom: '8px',
                flexShrink: 0
              }}
              crossOrigin="anonymous"
            />

            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '200px' }}>

              {/* Title */}
              <div style={{ textAlign: 'center', margin: '20px 0 30px' }}>
                <h2 style={{ fontSize: '28px', fontWeight: '900', color: '#000', margin: 0 }}>
                  {t('receiptVouchers.pdf.receiptVoucher')}
                </h2>
              </div>

              {/* Reference and Date Row */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', fontSize: '16px' }}>
                <div style={{ display: 'flex', gap: '5px' }}>
                  <span style={{ minWidth: '120px' }}>{t('receiptVouchers.pdf.reference')}:</span>
                  <span style={{ borderBottom: '1px dotted #000', padding: '0 10px', minWidth: '150px' }}>{voucher.reference || voucher.voucher_number || voucher.id || '—'}</span>
                </div>
                <div style={{ display: 'flex', gap: '5px' }}>
                  <span>{t('receiptVouchers.pdf.date')}:</span>
                  <span style={{ borderBottom: '1px dotted #000', padding: '0 10px', minWidth: '150px' }}>{formatDateEnglish(voucher.voucher_date)}</span>
                </div>
              </div>

              {/* Account / Safe Section */}
              <div style={{ marginBottom: '20px', fontSize: '16px' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '5px', textDecoration: 'underline' }}>{t('receiptVouchers.pdf.receivedAccount')}:</div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <span style={{ borderBottom: '1px dotted #000', flex: 1 }}>
                    {accountCode ? `${accountCode} - ` : ''} {accountName || '—'}
                  </span>
                </div>
              </div>

              {/* Receipt Data Section */}
              <div style={{ marginBottom: '20px' }}>


                {/* Received From */}
                <div style={{ marginBottom: '15px', fontSize: '16px' }}>
                  <div style={{ display: 'flex', gap: '10px', marginBottom: '5px' }}>
                    <span>{t('receiptVouchers.pdf.receivedFrom')}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '10px', marginBottom: details.length > 0 ? '8px' : '0' }}>
                    <span style={{ borderBottom: '1px dotted #000', padding: '0 10px', flex: 1 }}>
                      {voucher.received_from_name || '—'}
                    </span>
                  </div>
                  {/* Attempt to show the main customer/account from the first detail line as per example */}
                  {details.length > 0 && (
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <span>{t('receiptVouchers.pdf.account')}:</span>
                      <span style={{ borderBottom: '1px dotted #000', padding: '0 10px' }}>
                        {details[0].account?.code || '—'}
                      </span>
                      <span style={{ borderBottom: '1px dotted #000', padding: '0 10px', flex: 1 }}>
                        {isRTL ? (details[0].account?.name_ar || details[0].account?.name) : (details[0].account?.name || details[0].account?.name_ar) || '—'}
                      </span>
                    </div>
                  )}
                </div>

                {/* Amount in Words (Placeholder logic as we don't have a library, defaulting to number formatted prominently) */}
                <div style={{ marginBottom: '15px', fontSize: '16px', display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <span>{t('receiptVouchers.pdf.amount')}:</span>
                  <span style={{
                    border: '1px solid #000',
                    padding: '5px 15px',
                    flex: 1,
                    fontWeight: 'bold',
                    backgroundColor: '#f9f9f9'
                  }}>
                    {/* Ideally this should be words, but we fallback to clear number representation */}
                    ({formatCurrencyEnglish(totalAmount)} {t('receiptVouchers.pdf.only')})
                  </span>
                </div>
              </div>

              {/* Amount Details Table */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '5px', fontSize: '16px', textDecoration: 'underline' }}>{t('receiptVouchers.pdf.creditAccountsDetails')}:</div>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  border: '1px solid #000',
                  fontSize: '15px'
                }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f0f0f0' }}>
                      <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'center', width: '70%' }}>{t('receiptVouchers.pdf.statement')}</th>
                      <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'center', width: '30%' }}>{t('receiptVouchers.pdf.amount')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ height: '30px' }}>
                      <td style={{ border: '1px solid #000', padding: '5px 10px' }}>
                        {/* If simple details, list description. If multiple, maybe summarize */}
                        {t('receiptVouchers.pdf.creditAccountsDetails')}
                        {details.map((d, i) => (
                          <div key={i} style={{ fontSize: '13px', fontStyle: 'italic', marginTop: '2px' }}>
                            - {d.description || (d.account && (isRTL ? d.account.name_ar : d.account.name)) || '—'}
                          </div>
                        ))}
                      </td>
                      <td style={{ border: '1px solid #000', padding: '5px 10px', textAlign: 'center', verticalAlign: 'top' }}>
                        {formatCurrencyEnglish(totalAmount)}
                      </td>
                    </tr>
                    {/* Total Row */}
                    <tr style={{ backgroundColor: '#f0f0f0', fontWeight: 'bold' }}>
                      <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>{t('receiptVouchers.pdf.totalAmount')}</td>
                      <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>{formatCurrencyEnglish(totalAmount)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Notes */}
              <div style={{ marginBottom: '20px', fontSize: '16px' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>{t('receiptVouchers.pdf.notes')}:</div>
                <div style={{
                  border: '1px solid #000',
                  minHeight: '60px',
                  padding: '10px',
                  backgroundColor: '#f9f9f9',
                  whiteSpace: 'pre-wrap'
                }}>
                  {voucher.notes || '—'}
                </div>
              </div>

              {/* Approvals / Footer Section */}
              <div style={{ marginTop: 'auto', paddingTop: '20px' }}>
                <div style={{
                  border: '1px solid #000',
                  padding: '15px',
                  fontSize: '15px'
                }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '15px', textDecoration: 'underline' }}>{t('receiptVouchers.pdf.approvals')}</div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
                    <div style={{ display: 'flex', gap: '5px' }}>
                      <span>{t('receiptVouchers.pdf.status')}:</span>
                      <span style={{ fontWeight: 'bold' }}>{statusLabel(voucher.status)}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '5px' }}>
                      <span>{t('receiptVouchers.pdf.createdBy')}:</span>
                      <span>{voucher.created_by || 'Admin'}</span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0' }}>
                    <div style={{ width: '45%' }}>
                      <div style={{ marginBottom: '5px' }}>
                        <span>{t('receiptVouchers.pdf.receiverName')}:</span> .....................................................
                      </div>

                    </div>
                    <div style={{ width: '45%' }}>
                      <div style={{ marginBottom: '5px' }}>
                        <span>{t('receiptVouchers.pdf.reviewerName')}:</span> .....................................................
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>

            {/* Footer Image */}
            <div style={{
              position: 'absolute',
              bottom: '10px',
              left: '18px',
              right: '18px',
              flexShrink: 0
            }}>
              <img
                src={`${publicUrl}/FooterAce.png`}
                alt="Footer"
                style={{
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  borderTop: '4px double #000',
                  paddingTop: '8px',
                  flexShrink: 0
                }}
                crossOrigin="anonymous"
              />
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

const DropdownWithSearch = ({ isOpen, onToggle, searchTerm, onSearchChange, searchInputRef, items, pagination, currentPage, onPageChange, onSelect, displayValue, placeholder, label, renderItem }) => {
  const { t, i18n } = useTranslation('global');
  const isRTL = i18n.language === 'ar';

  const renderDropdownItemLabel = (item, codeField, nameArField, nameEnField) => {
    const code = item[codeField] || '';
    const name = isRTL
      ? (item[nameArField] || item[nameEnField] || '')
      : (item[nameEnField] || item[nameArField] || '');
    return `${code ? `${code} - ` : ''}${name}`;
  };

  const internalInputRef = useRef(null);
  const inputRef = searchInputRef || internalInputRef;

  useEffect(() => {
    if (isOpen && inputRef?.current) {
      const timer = setTimeout(() => {
        inputRef.current?.focus({ preventScroll: true });
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [isOpen, searchTerm, items, inputRef]);

  if (!isOpen) return null;

  return (
    <div style={{ position: 'absolute', zIndex: 10, top: '100%', left: 0, right: 0, background: '#1a1f2e', border: '1px solid #333', borderRadius: 8, marginTop: 4, maxHeight: 400, overflowY: 'auto' }}>
      <div style={{ padding: 8 }}>
        <input
          ref={inputRef}
          type="text"
          placeholder={`${t('quotations.add.search', { defaultValue: 'ابحث عن' })} ${label}`}
          value={searchTerm}
          onChange={(e) => {
            onSearchChange(e.target.value);
            setTimeout(() => {
              inputRef.current?.focus({ preventScroll: true });
            }, 0);
          }}
          onFocus={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          style={{ width: '100%', padding: 8, backgroundColor: '#14263b', border: '1px solid #444', borderRadius: 6, color: 'white', fontSize: 14 }}
        />
      </div>
      {pagination && pagination.last_page >= 1 && (
        <div style={{ padding: '8px', borderTop: '1px solid #333', background: '#14263b' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ color: '#ccc', fontSize: 11, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('common.pagination.page', { defaultValue: 'الصفحة' })} {pagination.current_page} {t('common.pagination.of', { defaultValue: 'من' })} {pagination.last_page}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
            <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (pagination.current_page > 1) onPageChange(1); }} disabled={pagination.current_page === 1} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>««</button>
            <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (pagination.current_page > 1) onPageChange(pagination.current_page - 1); }} disabled={pagination.current_page === 1} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>‹</button>
            {(() => {
              const pages = [];
              const current = pagination.current_page;
              const last = pagination.last_page;
              const showPages = 5;
              let start = Math.max(1, current - Math.floor(showPages / 2));
              let end = Math.min(last, start + showPages - 1);
              if (end - start < showPages - 1) start = Math.max(1, end - showPages + 1);
              for (let i = start; i <= end; i++) pages.push(i);
              return pages.map((page) => (
                <button key={page} type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onPageChange(page); }} style={{ padding: '4px 8px', backgroundColor: page === current ? '#AC2000' : '#1a1f2e', color: 'white', border: page === current ? '1px solid #AC2000' : '1px solid #444', borderRadius: '4px', cursor: 'pointer', fontSize: 10, fontWeight: page === current ? 'bold' : 'normal', minWidth: '28px' }}>{page}</button>
              ));
            })()}
            <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.current_page + 1); }} disabled={pagination.current_page === pagination.last_page} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>›</button>
            <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.last_page); }} disabled={pagination.current_page === pagination.last_page} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>»»</button>
          </div>
        </div>
      )}
      <div>
        {(Array.isArray(items) ? items : []).map((item) => (
          <div
            key={item.id}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onSelect(item.id);
            }}
            style={{
              padding: '12px 15px',
              cursor: 'pointer',
              borderTop: '1px solid #333',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              color: 'white',
              fontSize: '14px',
              transition: 'background-color 0.2s',
              direction: isRTL ? 'rtl' : 'ltr',
              textAlign: isRTL ? 'right' : 'left'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#14263b'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <span style={{ color: 'white' }}>
              {renderItem ? renderItem(item) : renderDropdownItemLabel(item, 'code', 'name_ar', 'name_en')}
            </span>
          </div>
        ))}
        {(!Array.isArray(items) || items.length === 0) && <div style={{ padding: 12, textAlign: 'center', color: '#bbb', direction: isRTL ? 'rtl' : 'ltr' }}>{t('quotations.add.noResults', { defaultValue: 'لا توجد نتائج' })}</div>}
      </div>
    </div>
  );
};

export default ReceiptVouchers;